import React from 'react';

/* ---------------------------------------------------------------------------
   TechMarks
   Every chip on the site gets a mark. Two kinds, and the distinction is honest:

     1. A product has a real brand logo. Those are the vendors' own SVGs,
        vendored into /public/assets/icons so a CDN pruning its catalogue can
        never silently blank a row again. (Simple Icons dropped OpenAI,
        TestFlight and BrowserStack; three chips went blank and stayed blank.)

     2. A protocol or a practice has no brand mark, so it gets a drawn glyph
        from the set below -- a pictogram of the thing itself, never a fake
        logo. Two to five strokes each, because they render at 13px.
   ------------------------------------------------------------------------ */

const icon = (file) => `${process.env.PUBLIC_URL}/assets/icons/${file}.svg`;

const TECH_ICONS = {
  'Kotlin':           icon('kotlin'),
  'Swift':            icon('swift'),
  'Dart':             icon('dart'),
  'TypeScript':       icon('typescript'),
  'JavaScript':       icon('javascript'),
  'Java':             icon('java'),
  'Python':           icon('python'),
  'Go':               icon('go'),
  'Flutter':          icon('flutter'),
  'Android':          icon('android'),
  'Android SDK':      icon('android'),
  'Espresso':         icon('android'),
  'iOS':              icon('apple'),
  'iOS / CallKit':    icon('apple'),
  'Apple':            icon('apple'),
  /* Apple ships TestFlight; no separate mark exists in any open icon set. */
  'TestFlight':       icon('apple'),
  'Prometheus':       icon('prometheus'),
  'Grafana':          icon('grafana'),
  'Elasticsearch':    icon('elasticsearch'),
  'Kibana':           icon('kibana'),
  'Fluentbit':        icon('fluentbit'),
  'Jenkins':          icon('jenkins'),
  'JUnit':            icon('junit'),
  'JUnit / Espresso': icon('junit'),
  'React':            icon('react'),
  'React.js':         icon('react'),
  'FastAPI':          icon('fastapi'),
  'PostgreSQL':       icon('postgresql'),
  'Firebase':         icon('firebase'),
  'MySQL':            icon('mysql'),
  'ProxySQL':         icon('mysql'),
  'Tailwind CSS':     icon('tailwindcss'),
  'Vite':             icon('vite'),
  'Nginx':            icon('nginx'),
  'Redis':            icon('redis'),
  'WebRTC':           icon('webrtc'),
  'WebSockets':       icon('socketdotio'),
  'SIP.js':           icon('javascript'),
  'n8n':              icon('n8n'),
  'Ansible':          icon('ansible'),
  'Google Sheets':    icon('googlesheets'),
  'Jira':             icon('jira'),
  'BrowserStack':     icon('browserstack'),
  'Play Store':       icon('googleplay'),
  'Google Play':      icon('googleplay'),
  'Vercel':           icon('vercel'),
  'Google Maps':      icon('googlemaps'),
  'ChatGPT':          icon('openai'),
  'Gemini':           icon('googlegemini'),
  'Cursor Pro':       icon('cursor'),
  'Jetpack Compose':  icon('jetpackcompose'),
  'Capacitor':        icon('capacitor'),
  'GitHub':           icon('github'),
  'LinkedIn':         icon('linkedin'),
};

/* --- Drawn glyphs for the things that have no logo ------------------------ */

const g = (...d) => d;

const CONCEPT_GLYPHS = {
  /* Braces: an interface you design rather than a product you install. */
  'API design': g(
    'M6.2 2.7c-1.2 0-1.7.5-1.7 1.7v1.5c0 1.2-.5 1.7-1.6 1.7 1.1 0 1.6.5 1.6 1.7v1.5c0 1.2.5 1.7 1.7 1.7',
    'M9.8 2.7c1.2 0 1.7.5 1.7 1.7v1.5c0 1.2.5 1.7 1.6 1.7-1.1 0-1.6.5-1.6 1.7v1.5c0 1.2-.5 1.7-1.7 1.7',
  ),
  /* A release tag. */
  'Semantic versioning': g(
    'M2.7 6.5 6.5 2.7h5.2a1.1 1.1 0 0 1 1.1 1.1v5.2l-3.8 3.8a1.2 1.2 0 0 1-1.7 0L2.7 8.2a1.2 1.2 0 0 1 0-1.7z',
    'M9.9 5.4h.01',
  ),
  'Crash triage': g(
    'M8 2.6 14 13.3H2z',
    'M8 6.6v3',
    'M8 11.6h.01',
  ),
  /* One block used twice. */
  'Code reuse': g(
    'M9.6 5.6V3.3a.7.7 0 0 0-.7-.7H3.3a.7.7 0 0 0-.7.7v5.6c0 .4.3.7.7.7h2.3',
    'M7.1 6.4h5.8c.4 0 .7.3.7.7v5.8c0 .4-.3.7-.7.7H7.1a.7.7 0 0 1-.7-.7V7.1c0-.4.3-.7.7-.7z',
  ),
  /* A page under a shield. */
  'WebView hardening': g(
    'M8 2.2 13.2 4v4.2c0 2.6-2 4.6-5.2 5.6-3.2-1-5.2-3-5.2-5.6V4z',
    'M5.1 6.4h5.8',
    'M5.1 8.9h3.6',
  ),
  /* Records, locked. */
  'Secure logging': g(
    'M2.4 4.7h4.4',
    'M2.4 8h3',
    'M2.4 11.3h2.2',
    'M9.9 8.4V7.1a1.9 1.9 0 0 1 3.8 0v1.3',
    'M9.2 8.4h5.2v4.4H9.2z',
  ),
  /* A handset: the call itself, not any one vendor's stack. */
  'SIP / VoIP': g(
    'M3.2 2.7h2.3l1.1 2.9-1.4 1a8.3 8.3 0 0 0 4.2 4.2l1-1.4 2.9 1.1v2.3a.9.9 0 0 1-1 .9A11.5 11.5 0 0 1 2.3 3.7a.9.9 0 0 1 .9-1z',
  ),
  /* Two peers and the relay that gets them connected. */
  'STUN/TURN / ICE': g(
    'M8 2.6a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2z',
    'M3.3 10.2a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2z',
    'M12.7 10.2a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2z',
    'M6.9 5.9 4.4 10.1',
    'M9.1 5.9l2.5 4.2',
    'M4.9 11.8h6.2',
  ),
  /* A codec: level meter. */
  'Opus': g(
    'M2.9 6.3v3.4',
    'M6.3 3.6v8.8',
    'M9.7 2.5v11',
    'M13.1 5.4v5.2',
  ),
  /* Layers: the same stream at three qualities. */
  'Simulcast / SVC': g(
    'M8 1.9 13.9 4.9 8 7.9 2.1 4.9z',
    'M2.5 8 8 10.8 13.5 8',
    'M2.5 10.9 8 13.7 13.5 10.9',
  ),
  /* A source, its echo, and the cancellation. */
  'AEC': g(
    'M2.2 6.3h1.6L5.7 4.4v7.2L3.8 9.7H2.2z',
    'M8.4 5.2a4 4 0 0 1 0 5.6',
    'M11 2.9a7.4 7.4 0 0 1 0 10.2',
    'M13.6 3.4 7.8 12.4',
  ),
  /* Shrink to fit. */
  'ProGuard / 16KB': g(
    'M6.6 2.9v3.7H2.9',
    'M2.9 2.9 6.4 6.4',
    'M9.4 13.1V9.4h3.7',
    'M13.1 13.1 9.6 9.6',
  ),
  /* A waveform, distinct from the codec meter. */
  'Web Audio API': g(
    'M1.6 8c1.7-4.9 3.4-4.9 5.1 0s3.4 4.9 5.1 0',
    'M11.8 8h2.6',
  ),
  /* Request out, response back. */
  'REST APIs': g(
    'M2.6 5.9h10.8',
    'M11 3.5 13.4 5.9 11 8.3',
    'M13.4 11.1H2.6',
    'M5 8.7 2.6 11.1 5 13.5',
  ),
  'AWS': g(
    'M4.9 12.4h6.4a3 3 0 0 0 .5-5.9 4.2 4.2 0 0 0-7.9-1.1 3.6 3.6 0 0 0 1 7z',
  ),
  /* Fan-out. */
  'AWS SNS': g(
    'M8 6.7a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6z',
    'M5.3 5.3a3.8 3.8 0 0 0 0 5.4',
    'M10.7 10.7a3.8 3.8 0 0 0 0-5.4',
    'M3.1 3.1a7 7 0 0 0 0 9.8',
    'M12.9 12.9a7 7 0 0 0 0-9.8',
  ),
};

/* "SIP / VoIP" -> "SIP", "Flutter (ExotelPlugin)" -> "Flutter", "X API" -> "X" */
const variants = (name) => {
  const base = name.split(/\s*[/(]/)[0].trim();
  return [name, base, base.replace(/\s+API$/i, '').trim()];
};

const resolveIconSrc = (name) => {
  for (const v of variants(name)) if (TECH_ICONS[v]) return TECH_ICONS[v];
  return null;
};

const resolveGlyph = (name) => {
  for (const v of variants(name)) if (CONCEPT_GLYPHS[v]) return CONCEPT_GLYPHS[v];
  return null;
};


const ConceptGlyph = ({ paths, size }) => (
  <svg
    className="tech-glyph"
    viewBox="0 0 16 16"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.35"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {paths.map((d) => <path key={d} d={d} />)}
  </svg>
);

/** A brand logo where one exists, otherwise the drawn glyph for the concept. */
export const TechIcon = ({ name, size = 16 }) => {
  const src = resolveIconSrc(name);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className="tech-icon"
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  const paths = resolveGlyph(name);
  return paths ? <ConceptGlyph paths={paths} size={size} /> : null;
};

/* A logo chip stays neutral; a glyph chip carries the accent, so a row of
   practices reads as deliberate rather than as a row that failed to load. */
export const Chip = ({ label }) => {
  const isConcept = !resolveIconSrc(label);
  return (
    <span className={`chip${isConcept ? ' chip--concept' : ''}`}>
      <TechIcon name={label} size={13} />
      {label}
    </span>
  );
};

